/*
Days view of the releases calendar on a phone (the month view there): one list of the days
with releases of the chosen type over the timeline's months, today first (even without a
release), then back in time as the page scrolls down. A row is a model: date, studio logo,
model name, studio name; it opens the same details window as a chip (data-pop). Expected
releases are not here — they are estimates, the timeline shows them. releasesPage.js scrolls
the page to today or to the chosen month; the list itself is built once per render.
*/
import { element } from './eventsData';
import { dayGroups, shortDate, todayKey } from './releasesData';
import { timelineMonths } from './releasesTimeline';
import { studioMark } from './releasesView';

/**
 * @param {{studios: Array, releases: Array}} data calendar from the radio
 * @param {{type: string}} state chosen type
 * @param {Date} now
 * @returns {HTMLElement}
 */
export function buildDays(data, state, now) {
    const studios = new Map(data.studios.map((studio) => [studio.id, studio]));
    const today = todayKey(now);
    const list = element('div', 'th-rel-days');
    dayGroups(data.releases, state.type, daysSpan(data, now)).forEach(({ day, releases }) => {
        const section = day === today ? todaySection : daySection;
        list.append(section(day, releases, studios));
    });
    return list;
}

/**
 * The span of the list: from the first month of the timeline to today.
 * @param {{releases: Array}} data calendar from the radio
 * @param {Date} now
 * @returns {{first: string, today: string}} YYYY-MM and YYYY-MM-DD
 */
export function daysSpan(data, now) {
    return { first: timelineMonths(data, now)[0], today: todayKey(now) };
}

/**
 * Where the page scrolls for a chosen month: the newest day of the month in the list or, when
 * the month has none, the first day before it (the oldest day at the latest).
 * @param {HTMLElement} list the list from buildDays
 * @param {string} month chosen YYYY-MM
 * @returns {HTMLElement}
 */
export function monthStart(list, month) {
    const days = [...list.querySelectorAll('[data-day]')];
    const inOrBefore = days.find((section) => section.dataset.day.slice(0, 7) <= month);
    return inOrBefore || days[days.length - 1];
}

function daySection(day, releases, studios) {
    const section = element('section', 'th-rel-days__day');
    section.dataset.day = day;
    section.append(...releases.map((release) => releaseRow(release, studios.get(release.studio))));
    return section;
}

function todaySection(day, releases, studios) {
    const section = daySection(day, releases, studios);
    section.dataset.today = 'true';
    const head = element('h2', 'th-rel-days__today', shortDate(day));
    head.append(element('span', 'th-rel-days__badge', 'Today'));
    section.prepend(head);
    if (releases.length === 0) {
        section.append(element('p', 'th-rel-days__none', 'No releases today'));
    }
    return section;
}

function releaseRow(release, studio) {
    const row = element('button', 'th-rel-days__row');
    row.type = 'button';
    row.dataset.pop = `release:${release.id}`;
    const names = element('span', 'th-rel-days__names');
    names.append(
        element('span', 'th-rel-days__name', release.name),
        element('span', 'th-rel-days__studio', studio?.name || release.studio),
    );
    const mark = studioMark(studio || { id: release.studio });
    row.append(element('span', 'th-rel-days__date', shortDate(release.date)), mark, names);
    return row;
}
